/**
 * The device's history of rounds on one device, as reactive state, and its
 * sync with the account (`docs/decisions/0059`).
 *
 * - **A guest** keeps it on the device (`local/history-io.ts`), the newest
 *   fifty rounds.
 * - **A signed-in device owner** also merges it with the account's: on every
 *   sign-in and every load that finds a session (`account.svelte.ts`), the
 *   device's rounds for that account go up and the account's come down (a
 *   union by id, the newest fifty kept, on both ends), and each round played
 *   afterwards goes up as it ends. So the same history shows on every device
 *   they sign in on.
 * - **Every round belongs to one player** (0059, "After review"): what is
 *   shown and what goes up is only the current owner's, a sign-in takes the
 *   guest's rounds and nobody else's, and a sign-out takes the account's
 *   rounds off the device. `local/history-io.ts` holds the rule.
 *
 * Nothing here touches a personal best, the solo statistics, the daily or a
 * record, and none of those touches this. Every request is fire-and-forget
 * apart from the one before a sign-out: the history on the device is already
 * written when one is sent, and a failure (offline, no accounts on this
 * server) leaves it as it is.
 */
import type { LocalSession } from '@interlock/protocol';

import {
  addLocalSession,
  adoptLocalSessions,
  bindLocalHistory as bindStore,
  loadLocalHistory,
  ownerOf,
  releaseLocalHistory as releaseStore,
  sessionsOf,
  uploadFor,
} from '../local/history-io.js';
import type { LocalHistoryStore } from '../local/history-io.js';
import { pushLocalSessions } from './account-io.js';

interface LocalHistoryView {
  /** Whose rounds these are: an account id, or `guest`. */
  owner: string;
  /** The owner's rounds, newest first. */
  sessions: readonly LocalSession[];
}

function viewOf(store: LocalHistoryStore): LocalHistoryView {
  return { owner: ownerOf(store), sessions: sessionsOf(store) };
}

export const localHistory: LocalHistoryView = $state(viewOf(loadLocalHistory()));

function show(store: LocalHistoryStore): void {
  const view = viewOf(store);
  localHistory.owner = view.owner;
  localHistory.sessions = view.sessions;
}

/** Read the device's store again (another tab, a test that seeded it). */
export function reloadLocalHistory(): void {
  show(loadLocalHistory());
}

/**
 * A round just finished: into the device's history at once, under the
 * current owner, and up to the account when there is one. `signedIn` is the
 * caller's to say, so this module does not import the account's state (which
 * imports this one).
 */
export function recordLocalRound(session: LocalSession, signedIn: boolean): void {
  const store = addLocalSession(session);
  show(store);
  if (signedIn && store.account !== null) void push(store.account, [session]);
}

/**
 * The device is signed in as `playerId`: from now on its rounds are the ones
 * shown and filed, and the guest's rounds are its. Called the moment a
 * sign-in or a load finds the session, before anything is sent.
 */
export function bindLocalHistory(playerId: string): void {
  show(bindStore(playerId));
}

/**
 * The account's rounds up, and the account's history down. Only the rounds
 * the device holds for the account it is signed in as are sent; another
 * player's never are. Answers whether the server took them.
 */
export async function syncLocalHistory(): Promise<boolean> {
  const store = loadLocalHistory();
  if (store.account === null) return false;
  return push(store.account, uploadFor(store, store.account));
}

/**
 * The device is signed out. `synced` says whether the server holds the
 * account's rounds (the sync just before the sign-out answered): then they
 * leave the device; otherwise they stay, hidden, for that account's next
 * sign-in here. The guest's rounds show from now on.
 */
export function releaseLocalHistory(synced: boolean): void {
  show(releaseStore(synced));
}

async function push(account: string, sessions: readonly LocalSession[]): Promise<boolean> {
  const result = await pushLocalSessions(sessions);
  if (result.status !== 'ok') return false;
  // Written only if the device is still signed in as `account`
  // (`adoptLocalSessions`): an answer that lands after a sign-out does not
  // bring the rounds back.
  show(adoptLocalSessions(account, result.value.sessions));
  return true;
}
